// Pesticide capture panel (design_handoff_estimates_mooring_pesticide Feature 3). When an operation is
// marked complete (submitOpen in app.js calls checkForPesticideUsage) and any of its parts is tagged
// Pesticide in Inventory, it asks to log the application (and take the quantity out of inventory), one
// part at a time via a small queue. Skip or × leaves Unlogged_Pesticide_Use on the operation so the use
// isn't lost. Opens its own overlay on top of the completion flow, without changing its timing.
// Split out of app.js (docs/split-plan.md, session 2). Needs nothing from app.js.
import { formatDateToYYYYMMDD } from './date-format';
import { DB_URL } from './firebaseConfig';
import { database } from './initFirebase';
import { adjustInventoryQuantity, fetchIdToken, getDB, idbCursorAll, logPesticideApplication, toastMessage, woLabel } from './utilities';
import { get, ref } from 'firebase/database';

let pesticideLogQueue = [];
let currentPesticideLogContext = null;

export async function checkForPesticideUsage(jobId, jobRecord) {
  try {
    if (!jobRecord || !jobRecord.Parts) return;
    const inventory = await idbCursorAll('inventoryData');
    const inventoryByPartNumber = new Map(inventory.map(i => [i.Part_Number, i]));

    const taggedUses = Object.values(jobRecord.Parts)
      .map(part => ({ part, item: inventoryByPartNumber.get(part.Part_Number) }))
      .filter(({ item }) => item && item.Pesticide);

    if (!taggedUses.length) return;

    // Append rather than replace — submitOpen() can also fire from the
    // realtime job-status listener when another device marks a job
    // complete, so a second call while the first job's panel(s) are still
    // queued/open must not silently drop them.
    const queueWasIdle = !currentPesticideLogContext && pesticideLogQueue.length === 0;
    pesticideLogQueue.push(...taggedUses.map(({ part, item }) => ({ jobId, jobRecord, part, item })));
    if (queueWasIdle) showNextPesticideLogPanel();
  } catch (e) {
    console.error('Error checking for pesticide usage:', e);
  }
}

function showNextPesticideLogPanel() {
  const next = pesticideLogQueue.shift();
  if (!next) {
    currentPesticideLogContext = null;
    return;
  }
  currentPesticideLogContext = next;
  renderPesticideLogPanel(next);
  document.getElementById('pesticide-log-overlay').classList.remove('hidden');
}

function renderPesticideLogPanel({ jobRecord, item }) {
  const applyDate = jobRecord.Start_Date || jobRecord.Schedule_Date || formatDateToYYYYMMDD(new Date());
  document.getElementById('pesticide-log-sub').textContent =
    `${woLabel(jobRecord.Work_Order || '', ' #')} · ${jobRecord.Boat_Name || ''} · marked done`;
  document.getElementById('pesticide-log-product-name').textContent = item.Description || item.Part_Number || '';
  document.getElementById('pesticide-log-product-sub').textContent =
    `${item.Part_Number || ''} · EPA ${item.EPA_Reg_Number || ''}`;
  document.getElementById('pesticide-log-qty').value = '0';
  document.getElementById('pesticide-log-apply-date').textContent = applyDate;
  document.getElementById('pesticide-log-end-date').textContent = applyDate;
  document.getElementById('pesticide-log-method').textContent = item.Default_Application_Method || '—';
  document.getElementById('pesticide-log-dosage').textContent = item.Default_Dosage_Rate || 'As supplied';
  document.getElementById('pesticide-log-target').textContent = item.Default_Target_Organism || '—';
  document.getElementById('pesticide-log-place').textContent = item.Default_Place_Of_Application || '—';
}

function closePesticideLogOverlay() {
  document.getElementById('pesticide-log-overlay').classList.add('hidden');
}

document.getElementById('pesticide-log-close')?.addEventListener('click', async () => {
  // Dismissing via the × is the same as Skip — either way the use went
  // unlogged, so the same audit flag applies.
  if (currentPesticideLogContext) {
    try {
      await flagUnloggedPesticideUse(currentPesticideLogContext.jobId);
    } catch (e) {
      console.error('Error flagging unlogged pesticide use:', e);
    }
  }
  closePesticideLogOverlay();
  showNextPesticideLogPanel();
});

document.getElementById('pesticide-log-qty-minus')?.addEventListener('click', () => {
  const input = document.getElementById('pesticide-log-qty');
  input.value = Math.max(0, (parseFloat(input.value) || 0) - 0.5);
});
document.getElementById('pesticide-log-qty-plus')?.addEventListener('click', () => {
  const input = document.getElementById('pesticide-log-qty');
  input.value = (parseFloat(input.value) || 0) + 0.5;
});

async function fetchYardProfile() {
  try {
    const snap = await get(ref(database, '/settings/yardProfile'));
    return snap.val() || {};
  } catch (e) {
    console.error('Error loading yard profile:', e);
    return {};
  }
}

// Skipping doesn't block anything — it just leaves a flag on the job so
// an unlogged regulated-material use isn't silently lost.
async function flagUnloggedPesticideUse(jobId) {
  const idToken = await fetchIdToken();
  await fetch(`${DB_URL}/data/${jobId}.json?auth=${idToken}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ Unlogged_Pesticide_Use: true })
  });
  const db = await getDB();
  const tx = db.transaction('jobData', 'readwrite');
  const store = tx.objectStore('jobData');
  const job = await new Promise((resolve, reject) => {
    const req = store.get(jobId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  if (job) {
    job.Unlogged_Pesticide_Use = true;
    await new Promise((resolve, reject) => {
      const req = store.put(job);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
}

document.getElementById('pesticide-log-skip-btn')?.addEventListener('click', async () => {
  if (!currentPesticideLogContext) return;
  const { jobId } = currentPesticideLogContext;
  try {
    await flagUnloggedPesticideUse(jobId);
  } catch (e) {
    console.error('Error flagging unlogged pesticide use:', e);
  }
  closePesticideLogOverlay();
  showNextPesticideLogPanel();
});

document.getElementById('pesticide-log-submit-btn')?.addEventListener('click', async () => {
  if (!currentPesticideLogContext) return;
  const { jobRecord, item } = currentPesticideLogContext;
  const qty = parseFloat(document.getElementById('pesticide-log-qty').value) || 0;
  if (qty <= 0) {
    toastMessage('Enter a quantity greater than 0', 'red');
    return;
  }
  try {
    const yard = await fetchYardProfile();
    const applyDate = document.getElementById('pesticide-log-apply-date').textContent;
    await logPesticideApplication({
      Apply_Date: applyDate,
      End_Date: document.getElementById('pesticide-log-end-date').textContent,
      EPA_Reg_Number: item.EPA_Reg_Number || '',
      Product_Name: item.Description || item.Part_Number || '',
      Part_Number: item.Part_Number || '',
      Quantity: qty,
      Units: item.Report_Units || 'gal',
      Dosage_Rate: item.Default_Dosage_Rate || '',
      Application_Method: item.Default_Application_Method || '',
      Target_Organism: item.Default_Target_Organism || '',
      Place_Of_Application: item.Default_Place_Of_Application || '',
      Source: `${woLabel(jobRecord.Work_Order || '', ' #')} · ${jobRecord.Boat_Name || ''}`,
      Address: yard.Address || '',
      Municipality: yard.Municipality || '',
      County: yard.County || '',
      Zip: yard.Zip || '',
    });
    await adjustInventoryQuantity(item.id, -qty);
    toastMessage('Logged to pesticide usage report', 'green', 2500);
  } catch (e) {
    console.error('Error logging pesticide application:', e);
    toastMessage('Error logging pesticide application', 'red');
    return;
  }
  closePesticideLogOverlay();
  showNextPesticideLogPanel();
});
